import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";

const SESSION_KEY = "fg_analytics_sid";
const UTM_KEY = "fg_utm_attribution";
const HEARTBEAT_INTERVAL_MS = 30000; // 30s
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto.randomUUID && crypto.randomUUID()) ||
            `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `nostore-${Date.now()}`;
  }
}

/**
 * First-touch UTM attribution.
 * - Reads UTM params from current URL.
 * - If present, persists them in sessionStorage for the rest of the session.
 * - If absent, returns whatever was captured on first hit (or empty).
 */
function getUtmAttribution(search) {
  let captured = {};
  try {
    const stored = sessionStorage.getItem(UTM_KEY);
    if (stored) captured = JSON.parse(stored) || {};
  } catch {
    captured = {};
  }

  try {
    const params = new URLSearchParams(search || "");
    const fresh = {};
    let hasAny = false;
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) {
        fresh[k] = v.slice(0, 120);
        hasAny = true;
      }
    });
    if (hasAny) {
      // First-touch wins: only overwrite if nothing was captured yet
      const merged = Object.keys(captured).length ? captured : fresh;
      try { sessionStorage.setItem(UTM_KEY, JSON.stringify(merged)); } catch { /* sessionStorage unavailable */ }
      return merged;
    }
  } catch {
    // ignore
  }

  return captured;
}

/**
 * Anonymous, lightweight page-view + time-on-site tracker.
 * - Sends one pageview per route change.
 * - Sends a heartbeat every 30s while the tab is visible.
 * - Backend caps duration at 30 min/visit to ignore forgotten tabs.
 * - Captures + persists UTM parameters for first-touch attribution.
 */
const AnalyticsTracker = () => {
  const location = useLocation();
  const visitIdRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const sessionId = getOrCreateSessionId();
    const utm = getUtmAttribution(location.search);

    const sendPageview = async () => {
      try {
        const res = await axios.post(`${API}/analytics/pageview`, {
          session_id: sessionId,
          path: location.pathname + location.search,
          referrer: document.referrer || "",
          utm_source: utm.utm_source || "",
          utm_medium: utm.utm_medium || "",
          utm_campaign: utm.utm_campaign || "",
          utm_term: utm.utm_term || "",
          utm_content: utm.utm_content || "",
        });
        visitIdRef.current = res.data?.visit_id || null;
      } catch {
        // Silent — analytics should never block UX
      }
    };

    const sendHeartbeat = async () => {
      if (!visitIdRef.current) return;
      if (document.hidden) return;
      try {
        await axios.post(`${API}/analytics/heartbeat`, {
          visit_id: visitIdRef.current,
        });
      } catch {
        // Silent
      }
    };

    sendPageview();

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Final heartbeat to lock in duration before route change
      sendHeartbeat();
    };
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
